import { defineStore } from "pinia";

import { auth } from "../firebase/firebaseConfig";
import { useUploadStore } from "./upload";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInAnonymously,
  signInWithPopup,
  linkWithPopup,
} from "firebase/auth";

import {
  getDatabase,
  ref,
  update,
  increment,
} from "firebase/database";

import { getAnalytics, logEvent } from "firebase/analytics";

import mixpanel from "mixpanel-browser";

const provider = new GoogleAuthProvider();
const mixpanelToken = process.env.VUE_APP_MIXPANEL_TOKEN;

const countryToLanguage = {
  US: "en", GB: "en", AU: "en", NZ: "en", IE: "en", CA: "en",
  DE: "de", AT: "de", CH: "de",
  FR: "fr", BE: "fr",
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es",
  IT: "it",
  JP: "ja",
  NL: "nl",
  PL: "pl",
  PT: "pt", BR: "pt",
  RU: "ru", BY: "ru", KZ: "ru",
  SE: "sv",
  TR: "tr",
  IN: "hi",
  FI: "fi",
  DK: "da",
  NO: "no",
  CZ: "cs",
  RO: "ro",
  HU: "hu",
  IR: "fa",
  MY: "ms",
  TN: "ta",
  EG: "ar", SA: "ar", AE: "ar", MA: "ar", DZ: "ar",
  ID: "ms",
};

if (process.env.VUE_APP_COLLECT_DATA === "false") {
  const analytics = getAnalytics();
  logEvent(analytics, "Sandbox");
  mixpanel.init(mixpanelToken, { debug: true });
  mixpanel.people.set({ SignupType: "sandbox", trackingVersion: "0.0.1" });
} else {
  const analytics = getAnalytics();
  logEvent(analytics, "Init Prod");
  mixpanel.init(mixpanelToken);
  mixpanel.people.set({ SignupType: "production", trackingVersion: "0.0.1" });
}

const db = getDatabase();

export const useUserStore = defineStore("user", {
  state: () => ({
    user: null,
    language: {},
    langOptions: [
      { name: "English", code: "en" },
      { name: "Arabic", code: "ar" },
      { name: "Catalan", code: "ca" },
      { name: "Czech", code: "cs" },
      { name: "Danish", code: "da" },
      { name: "Dutch", code: "nl" },
      { name: "Farsi", code: "fa" },
      { name: "Finnish", code: "fi" },
      { name: "French", code: "fr" },
      { name: "German", code: "de" },
      { name: "Hindi", code: "hi" },
      { name: "Hungarian", code: "hu" },
      { name: "Italian", code: "it" },
      { name: "Japanese", code: "ja" },
      { name: "Malay", code: "ms" },
      { name: "Norwegian", code: "no" },
      { name: "Polish", code: "pl" },
      { name: "Portuguese (Brazil)", code: "pt" },
      { name: "Romanian", code: "ro" },
      { name: "Russian", code: "ru" },
      { name: "Spanish", code: "es" },
      { name: "Swedish", code: "sv" },
      { name: "Tamil", code: "ta" },
      { name: "Telegu", code: "te" },
      { name: "Turkish", code: "tr" },
    ],
  }),
  actions: {
    async anonymSignup() {
      const user = auth.currentUser;
      const date = Date.now();
      if (user) {
        return;
      }
      const response = await signInAnonymously(auth);
      if (response) {
        const {
          displayName,
          email,
          emailVerified,
          isAnonymous,
          metadata,
          photoURL,
          uid,
        } = response.user;
        this.setUser({
          displayName,
          email,
          emailVerified,
          isAnonymous,
          metadata,
          photoURL,
          uid,
        });
        update(ref(db, "users/" + response.user.uid), {
          lastLogin: date,
          totalLogins: increment(1),
        });
        mixpanel.identify(response.user.uid);
        mixpanel.track("Sign Up");
      } else {
        throw new Error("signup failed");
      }
    },

    async convertToGoogle() {
      const uploadStore = useUploadStore();
      mixpanel.people.set({ SignupProvider: "startedConvertGoogle" });
      const response = await linkWithPopup(auth.currentUser, provider);
      if (response) {
        const {
          displayName,
          email,
          emailVerified,
          isAnonymous,
          metadata,
          photoURL,
          uid,
        } = response.user;
        this.setUser({
          displayName,
          email,
          emailVerified,
          isAnonymous,
          metadata,
          photoURL,
          uid,
        });
        const data = {
          id: uid,
          email: email,
          userType: "extendedFree",
        };
        uploadStore.writeDataToDB(uid, data, "users/" + uid + "/auth");
        mixpanel.people.set({ SignupProvider: "Google" });
      } else {
        throw new Error("signup failed");
      }
    },

    async loginGoogle() {
      const response = await signInWithPopup(auth, provider);
      if (response) {
        const {
          displayName,
          email,
          emailVerified,
          isAnonymous,
          metadata,
          photoURL,
          uid,
        } = response.user;
        this.setUser({
          displayName,
          email,
          emailVerified,
          isAnonymous,
          metadata,
          photoURL,
          uid,
        });
      } else {
        throw new Error("login failed");
      }
    },

    async logout() {
      await signOut(auth);
      this.setUser(null);
    },

    setUser(payload) {
      this.user = payload;
    },

    autoSelectLanguage(countryCode) {
      if (!countryCode) return;
      if ("code" in this.language) return;
      const langCode = countryToLanguage[countryCode];
      if (!langCode) return;
      const match = this.langOptions.find((l) => l.code === langCode);
      if (match) {
        this.language = match;
      }
    },
  },
  persist: true,
});
